import React, {memo} from 'react';
import {useSelector} from 'react-redux';
import {AdBannerData, AdBannersConfig} from '@/app/interfaces/ad';
import {selectDomainConfig, selectNoAds} from "@/app/store/config/selectors";
import {AdBannerEntry, AdBannerEntryProps} from '../AdBannerEntry';
import bannersConfig from '@/app/banners.json';

type BannerZoneName = keyof AdBannersConfig['desktop']['slots'] | keyof AdBannersConfig['mobile']['slots'];

interface AdBannerProps extends Omit<AdBannerEntryProps, 'data' | 'className'> {
	className?: string;
	entryClassName?: string;
	interfaceName: 'desktop' | 'mobile';
	zoneName: BannerZoneName;
	zoneDataReducer?(data: AdBannerData | AdBannerData[]): AdBannerData | AdBannerData[] | null;
}

export const AdBanner = memo<AdBannerProps>(props => {
	const {className, entryClassName, interfaceName, zoneName, zoneDataReducer, ...proxyProps} = props;

	const {bannersSet} = useSelector(selectDomainConfig);
	const noAds = useSelector(selectNoAds);

	const slots = bannersConfig[bannersSet as keyof typeof bannersConfig]?.[interfaceName].slots as any;
	let data = slots[zoneName] as AdBannerData | AdBannerData[] | undefined | null;

	if (data && zoneDataReducer) {
		data = zoneDataReducer(data);
	}

	if (!data || noAds) {
		return <div className={className} suppressHydrationWarning />;
	}

	if (Array.isArray(data)) {
		return (
			<div className={className} suppressHydrationWarning>
				{data.map((item, index) => (
					// eslint-disable-next-line react/no-array-index-key
					<AdBannerEntry
						key={index}
						className={entryClassName}
						data={item}
						interfaceName={interfaceName}
						{...proxyProps}
					/>
				))}
			</div>
		);
	}

	return (
		<div className={className} suppressHydrationWarning>
			<AdBannerEntry className={entryClassName} data={data} interfaceName={interfaceName} {...proxyProps} />
		</div>
	);
});

AdBanner.displayName = 'AdBanner';
